import { useContext, useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { useTranslation } from 'react-i18next';
import { Context } from '../../../Context';
import { ApartmentViewContext } from '../context/ApartmentViewContext';
import Zoom from '@mui/material/Zoom';
import CustomToolTip from '../../ToolTipInfoCustomMUI';
import {
	arrangeModeName,
	hotSpotModeName,
	infoModeName,
	insolationModeName,
	isometricModeName,
	stylesModeName,
} from '../context/contextStrings';
import { getRotateToNorthAngleDeg } from '../utils/geometryUtils';
import { ReactComponent as IconNorth } from '../assets/icons/icon-north.svg';
import { ReactComponent as IconSun } from '../assets/icons/icon-sun.svg';
import { ReactComponent as IconChair } from '../assets/icons/icon-armchair.svg';
import { ReactComponent as IconViewPoint } from '../assets/icons/icon-viewpoint.svg';
import { ReactComponent as IconIsometric } from '../assets/icons/icon-isometric.svg';
import { ReactComponent as IconHeart } from '../assets/icons/icon-heart.svg';
import { ReactComponent as IconDownload } from '../assets/icons/icon-download.svg';
import PdfModal from './PdfModal';
import ApartmentQueryButton from './ApartmentQueryButton';
import apart_styles from '../../../styles/aprt.module.scss';
import buttons_styles from '../../../styles/buttons.module.scss';
import ToggleButtonsGroup from '../../_shared/ToggleButtonsGroup';

function PlanButtonsNew(
	{
		// apartmentData,
		// dataUrl,
		// projectSetup
	}
) {
	const {
		activeBuilding,
		edificesData,
		favoriteApartmentUUIDs,
		toggleFavoriteApartmentHandler,
		projectSetup,
		activeApartmentData: apartmentData,
		dataUrl,
	} = useContext(Context);
	const {
		apartmentViewMode,
		setApartmentViewMode,
		doApartmentsHaveHotSpots,
		doApartmentsHaveVersions,
		doApartmentsHaveIsometricViews,
		setApartmentViewInteractions,
		setInfoTooltipData,
		isometricAngle,
	} = useContext(ApartmentViewContext);
	const { t } = useTranslation();

	const rotationToNorthAngleDeg = getRotateToNorthAngleDeg(
		apartmentData.azimuth
	);
	const isRotatedToNorth = apartmentViewMode.rotatedToNorth;
	const selectedMode = apartmentViewMode.mode;
	const isItHotSpotMode = selectedMode === hotSpotModeName;
	const isItIsometricMode = selectedMode === isometricModeName;
	const iconNorthRef = useRef(null);

	const {
		render_top: isTopRenderEnabled,
		floorplan_insolation: isInsolationEnabled,
		render_360: isAxonometryEnabled,
		compartments_versioning: isVersioningEnabled,
		editing: isEditingEnabled,
		floorplan_pdf: isPdfSheetEnabled,
	} = projectSetup.apartments;
	const { render_ai: isHotSpotModeEnabled } = projectSetup.interiors;

	const checkCamerasForHotSpots = (apartmentData) => {
		if (!apartmentData || !doApartmentsHaveHotSpots) return false;

		const hasAIViewCamera = apartmentData.tiers.some((tier) =>
			tier.cameras.some((camera) => camera.name.includes('id'))
		); //TODO: add to context stings
		//TODO: this is temporary solution, should be changed to check if camera name string starts with determined string

		if (hasAIViewCamera) return true;

		return false;
	};
	const checkCamerasIsometricView = (apartmentData) => {
		if (!apartmentData || !doApartmentsHaveIsometricViews) return false;

		const hasIsometricViewCamera = apartmentData.tiers.some((tier) =>
			tier.cameras.some((camera) => camera.name.includes('axonometric'))
		); //TODO: add to context stings

		if (hasIsometricViewCamera) return true;

		return false;
	};
	const checkCompartmentsForVersions = (apartmentData) => {
		if (!apartmentData || !doApartmentsHaveVersions) return false;

		const versions = apartmentData.compartments.map(
			(compartments) => +compartments.versions
		);

		if (versions.some((number) => number > 1)) return true;

		return false;
	};

	const [areHotSpotsPresent, setAreHotSpotsPresent] = useState(false);
	const [areVersionsPresent, setAreVersionsPresent] = useState(false);
	const [isIsometricViewPresent, setIsIsometricViewPresent] = useState(false);

	useEffect(() => {
		if (!apartmentData) return;

		setAreHotSpotsPresent(checkCamerasForHotSpots(apartmentData));
		setAreVersionsPresent(checkCompartmentsForVersions(apartmentData));
		setIsIsometricViewPresent(checkCamerasIsometricView(apartmentData));
	}, [apartmentData]);

	const handleRotate = () => {
		setApartmentViewMode(({ rotatedToNorth, ...rest }) => ({
			...rest,
			rotatedToNorth: !rotatedToNorth,
		}));
	};

	const scrollToElement = (elementId) => {
		if (elementId) {
			const element = document.getElementById(elementId);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: 'end' });
			} else {
				console.warn(
					`[PlanButtons] scrollToElement: Element with ID "${elementId}" not found.`
				);
			}
		}
	};

	const handleViewModeChange = (value) => {
		setApartmentViewInteractions((prev) => ({
			...prev,
			selectedCompartmentInd: null,
		}));
		setApartmentViewMode(({ mode, rotatedToNorth, ...rest }) => ({
			...rest,
			mode: value,
			rotatedToNorth: false,
		}));
		scrollToElement('compartments-block');
	};

	const updateTooltipOnHover = (str, e) => {
		if (e) {
			const rect = e.target.getBoundingClientRect();
			const position = {
				top: rect.top,
				bottom: rect.bottom,
				left: rect.left,
				right: rect.right,
				width: rect.width,
				height: rect.height,
			};

			str === null
				? setInfoTooltipData({ type: '', hovered: false, position: null })
				: setInfoTooltipData({ type: str, hovered: true, position });
		}
	};

	const getIconNorthTransform = () => {
		if (!isItIsometricMode && !isRotatedToNorth)
			return `rotate(${-rotationToNorthAngleDeg}deg)`;
		if (isItIsometricMode && !isRotatedToNorth)
			return `rotate(${isometricAngle}deg)`;
		return 'rotate(0deg)';
	};

	useEffect(() => {
		const targetAngle = (() => {
			if (!isItIsometricMode && !isRotatedToNorth)
				return -rotationToNorthAngleDeg;
			if (isItIsometricMode && !isRotatedToNorth) return isometricAngle;
			return 0;
		})();

		gsap.to(iconNorthRef.current, {
			// shortRotation: targetAngle, // warning na konsoli `Invalid property shortRotation set to 0 Missing plugin? gsap.registerPlugin()`
			rotation: targetAngle + '_short',
			duration: 0.5,
			ease: 'power1.inOut',
			transformOrigin: 'center',
		});
	}, [
		isRotatedToNorth,
		isItIsometricMode,
		rotationToNorthAngleDeg,
		isometricAngle,
	]);

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'row',
				// position: 'relative',
				// top: 'calc(93px - 4rem)',
				alignSelf: 'flex-end',
			}}
		>
			<div
				style={{
					display: 'flex',
					gap: '1rem',
					lineHeight: '2rem',
					alignItems: 'center',
					justifyContent: 'center',
					marginRight: '6rem',
					fontSize: '1rem',
				}}
			>
				<span>{t('mode')}:</span>
				<ToggleButtonsGroup>
					<>
						{isHotSpotModeEnabled && doApartmentsHaveHotSpots && (
							<CustomToolTip
								title={
									<>
										<p className={apart_styles.infoTooltip__paragraph}>
											<strong>{t('hot_spots_mode_info_1')}</strong>
										</p>
										<p className={apart_styles.infoTooltip__paragraph}>
											{t('hot_spots_mode_info_2')}
										</p>
									</>
								}
								slots={{
									transition: Zoom,
								}}
								arrow
							>
								<button
									className={`${buttons_styles['bim_button']} ${
										selectedMode === hotSpotModeName
											? buttons_styles['bim_button--selected']
											: ''
									}`}
									onClick={() => handleViewModeChange(hotSpotModeName)}
									onMouseEnter={(e) => updateTooltipOnHover(hotSpotModeName, e)}
									onMouseLeave={(e) => updateTooltipOnHover(null, e)}
									disabled={!areHotSpotsPresent}
								>
									<IconViewPoint
										className={buttons_styles['bim_button__icon']}
									/>
								</button>
							</CustomToolTip>
						)}
						{/* <CustomToolTip
							title={
								<>
									<p className={apart_styles.infoTooltip__paragraph}>
										<strong>{t('info_mode_info_1')}</strong>
									</p>
									<p className={apart_styles.infoTooltip__paragraph}>
										{t('info_mode_info_2')}
									</p>
								</>
							}
							slots={{
								transition: Zoom,
							}}
							arrow
						>
							<button
								className={`${buttons_styles['bim_button--small_font']} ${
									selectedMode === infoModeName
										? buttons_styles['bim_button--selected']
										: ''
								}`}
								onClick={() => handleViewModeChange(infoModeName)}
								onMouseEnter={(e) => updateTooltipOnHover(infoModeName, e)}
								onMouseLeave={(e) => updateTooltipOnHover(null, e)}
								//   disabled={selectedHotSpot}
							>
								m<sup>2</sup>
							</button>
						</CustomToolTip> */}
						{isInsolationEnabled && (
							<CustomToolTip
								title={
									<>
										<p className={apart_styles.infoTooltip__paragraph}>
											<strong>{t('insolation_mode_info_1')}</strong>
										</p>
										<p className={apart_styles.infoTooltip__paragraph}>
											{t('insolation_mode_info_2')}
										</p>
									</>
								}
								slots={{
									transition: Zoom,
								}}
								arrow
							>
								<button
									className={`${buttons_styles['bim_button']} ${
										selectedMode === insolationModeName
											? buttons_styles['bim_button--selected']
											: ''
									}`}
									onClick={() => handleViewModeChange(insolationModeName)}
									onMouseEnter={(e) =>
										updateTooltipOnHover(insolationModeName, e)
									}
									onMouseLeave={(e) => updateTooltipOnHover(null, e)}
									// disabled={selectedHotSpot}
								>
									<IconSun className={buttons_styles['bim_button__icon']} />
								</button>
							</CustomToolTip>
						)}
						{isVersioningEnabled && doApartmentsHaveVersions && (
							<CustomToolTip
								title={
									<>
										<p className={apart_styles.infoTooltip__paragraph}>
											<strong>{t('arrange_mode_info_1')}</strong>
										</p>
										<p className={apart_styles.infoTooltip__paragraph}>
											{t('arrange_mode_info_2')}
										</p>
									</>
								}
								slots={{
									transition: Zoom,
								}}
								arrow
							>
								<button
									className={`${buttons_styles['bim_button']} ${
										selectedMode === arrangeModeName
											? buttons_styles['bim_button--selected']
											: ''
									}`}
									onClick={() => handleViewModeChange(arrangeModeName)}
									onMouseEnter={(e) => updateTooltipOnHover(arrangeModeName, e)}
									onMouseLeave={(e) => updateTooltipOnHover(null, e)}
									disabled={!areVersionsPresent}
								>
									<IconChair className={buttons_styles['bim_button__icon']} />
								</button>
							</CustomToolTip>
						)}
						{isAxonometryEnabled && doApartmentsHaveIsometricViews && (
							<CustomToolTip
								title={
									<>
										<p className={apart_styles.infoTooltip__paragraph}>
											<strong>{t('isometric_mode_info_1')}</strong>
										</p>
										<p className={apart_styles.infoTooltip__paragraph}>
											{t('isometric_mode_info_2')}
										</p>
									</>
								}
								slots={{
									transition: Zoom,
								}}
								arrow
							>
								<button
									className={`${buttons_styles['bim_button']} ${
										selectedMode === isometricModeName
											? buttons_styles['bim_button--selected']
											: ''
									}`}
									onClick={() => handleViewModeChange(isometricModeName)}
									onMouseEnter={(e) =>
										updateTooltipOnHover(isometricModeName, e)
									}
									onMouseLeave={(e) => updateTooltipOnHover(null, e)}
									disabled={!isIsometricViewPresent}
								>
									<IconIsometric
										className={buttons_styles['bim_button__icon']}
										style={{ transform: `rotate(180deg)` }}
									/>
								</button>
							</CustomToolTip>
						)}
					</>
				</ToggleButtonsGroup>
			</div>
			<div
				style={{
					display: 'flex',
					gap: '1rem',
					lineHeight: '2rem',
					alignItems: 'center',
					justifyContent: 'center',
					marginRight: '2rem', //inny margin niż w trybach!
					fontSize: '1rem',
				}}
			>
				<span>{t('view')}:</span>
				{isTopRenderEnabled && (
					<ToggleButtonsGroup>
						<CustomToolTip
							title={
								<p className={apart_styles.infoTooltip__paragraph}>
									<strong>{t('three_d_plan_info_2')}</strong>
								</p>
							}
							slots={{
								transition: Zoom,
							}}
							arrow
						>
							<button
								className={`${buttons_styles['bim_button--small_font']} ${
									selectedMode === stylesModeName
										? ''
										: buttons_styles['bim_button--selected']
								}`}
								onClick={() =>
									handleViewModeChange(
										isHotSpotModeEnabled && doApartmentsHaveHotSpots
											? hotSpotModeName
											: infoModeName
									)
								}
								onMouseEnter={(e) => updateTooltipOnHover(stylesModeName, e)}
								onMouseLeave={(e) => updateTooltipOnHover(null, e)}
								//   disabled={selectedHotSpot}
							>
								2D
							</button>
						</CustomToolTip>
						<CustomToolTip
							title={
								<p className={apart_styles.infoTooltip__paragraph}>
									<strong>{t('three_d_plan_info_1')}</strong>
								</p>
							}
							slots={{
								transition: Zoom,
							}}
							arrow
						>
							<button
								className={`${buttons_styles['bim_button--small_font']} ${
									selectedMode === stylesModeName
										? buttons_styles['bim_button--selected']
										: ''
								}`}
								onClick={() => handleViewModeChange(stylesModeName)}
								onMouseEnter={(e) => updateTooltipOnHover(stylesModeName, e)}
								onMouseLeave={(e) => updateTooltipOnHover(null, e)}
								//   disabled={selectedHotSpot}
							>
								3D
							</button>
						</CustomToolTip>
					</ToggleButtonsGroup>
				)}
				<CustomToolTip
					title={
						<p className={apart_styles.infoTooltip__paragraph}>
							<strong>{t('rotate_to_north_info')}</strong>
						</p>
					}
					slots={{
						transition: Zoom,
					}}
					arrow
				>
					<button
						className={`${buttons_styles['bim_button']} ${
							isRotatedToNorth ? buttons_styles['bim_button--selected'] : ''
						}`}
						onClick={() => handleRotate()}
						onMouseEnter={(e) => updateTooltipOnHover('rotateToNorth', e)}
						onMouseLeave={(e) => updateTooltipOnHover(null, e)}
						disabled={isItHotSpotMode}
					>
						<IconNorth
							ref={iconNorthRef}
							className={buttons_styles['bim_button__icon']}
						/>
					</button>
				</CustomToolTip>
			</div>
		</div>
	);
}

export default PlanButtonsNew;
